import type { LiveEvent, LiveMessage } from '@cc/api-types';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { setLiveStatus } from './liveStatus';
import { qk } from './queries';

const KINDS = new Set<LiveEvent['kind']>(['message', 'conversation', 'booking', 'needs_you', 'activity', 'guide', 'settings', 'assistant', 'ping']);

const isEvent = (value: unknown): value is LiveEvent =>
  typeof value === 'object' && value !== null && KINDS.has((value as { kind: LiveEvent['kind'] }).kind);

/** A socket message is a LiveMessage (the events one change committed); anything else is ignored. */
export function parseLiveMessage(raw: unknown): LiveEvent[] {
  if (typeof raw !== 'string') return [];
  try {
    const value: unknown = JSON.parse(raw);
    const events: unknown[] = Array.isArray(value) ? (value as LiveMessage) : [value];
    return events.filter(isEvent);
  } catch {
    return []; // "pong" and anything else that is not ours
  }
}

/**
 * Map one server event to the screens that show that thing. The dashboard's "Keta right now",
 * the Leads table and the booking drawers all show what Keta does next, so most events touch them.
 */
export function applyLiveEvent(client: QueryClient, event: LiveEvent) {
  const inv = (queryKey: readonly unknown[]) => void client.invalidateQueries({ queryKey });
  switch (event.kind) {
    case 'message':
    case 'conversation':
      inv(qk.chats);
      inv(event.conversationId ? qk.chat(event.conversationId) : qk.chatAll);
      inv(qk.leads);
      inv(qk.leadAll);
      inv(qk.dashboard);
      inv(qk.bookings);
      inv(qk.bookingAll);
      break;
    case 'booking':
      inv(qk.bookings);
      inv(qk.calendarAll);
      inv(qk.dashboard);
      inv(event.bookingId ? qk.booking(event.bookingId) : qk.bookingAll);
      inv(event.bookingId ? qk.lead(event.bookingId) : qk.leadAll);
      inv(qk.leads);
      inv(qk.freeGuidesAll);
      break;
    case 'needs_you':
      inv(qk.needsYou);
      inv(qk.me);
      inv(qk.dashboard);
      inv(qk.leads);
      inv(qk.leadAll);
      break;
    case 'activity':
      inv(qk.dashboard);
      inv(event.bookingId ? qk.booking(event.bookingId) : qk.bookingAll);
      inv(event.bookingId ? qk.lead(event.bookingId) : qk.leadAll);
      break;
    case 'guide':
      inv(qk.guides);
      inv(qk.guideAll);
      inv(qk.bookingAll);
      break;
    case 'settings':
      inv(qk.settings);
      inv(qk.dashboard);
      // Knowledge changes arrive as settings events too (a mined proposal, an edit on another screen).
      inv(qk.knowledge);
      break;
    case 'assistant':
      inv(qk.me);
      break;
    case 'ping':
      break;
  }
}

/**
 * Keeps the dashboard live: one WebSocket to /api/live, reconnecting with backoff. Each event
 * invalidates the matching queries. While the socket is down the query defaults poll instead.
 */
export function useLive() {
  const client = useQueryClient();

  useEffect(() => {
    if (__MOCK__) {
      let unsubscribe: (() => void) | null = null;
      let cancelled = false;
      void import('../mock/bus').then(({ subscribe }) => {
        if (cancelled) return;
        unsubscribe = subscribe((event) => applyLiveEvent(client, event));
        setLiveStatus('open');
      });
      return () => {
        cancelled = true;
        unsubscribe?.();
        setLiveStatus('closed');
      };
    }

    let socket: WebSocket | null = null;
    let timer: number | undefined;
    let attempt = 0;
    let stopped = false;
    let hadConnection = false;

    const connect = () => {
      setLiveStatus('connecting');
      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const ws = new WebSocket(`${scheme}://${window.location.host}/api/live`);
      socket = ws;
      ws.onopen = () => {
        attempt = 0;
        setLiveStatus('open');
        // Anything that changed while we were away arrives as one refresh.
        if (hadConnection) void client.invalidateQueries();
        hadConnection = true;
      };
      ws.onmessage = (msg) => {
        for (const event of parseLiveMessage(msg.data)) applyLiveEvent(client, event);
      };
      ws.onerror = () => ws.close();
      ws.onclose = () => {
        if (socket !== ws) return;
        socket = null;
        setLiveStatus('closed');
        // Re-run the queries now so their polling interval takes over straight away.
        void client.invalidateQueries({ refetchType: 'active' });
        if (stopped) return;
        const delay = Math.min(30_000, 1_000 * 2 ** attempt) * (0.75 + Math.random() * 0.5);
        attempt += 1;
        timer = window.setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      const ws = socket;
      socket = null;
      ws?.close();
      setLiveStatus('closed');
    };
  }, [client]);
}
